// src/pages/Items.tsx
import React, { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Chip,
} from '@mui/material';
import { StockItem } from '../../../models/stocks';
import { getBarInventory_sales } from '../../../api/products/product';
import { getTlocations_production_centre } from '../../../api/stock-transfer/stock-transfer'; // Import the branches API
import { formatCurrencyN } from '../../../common/utils_t';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateRange } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

const BarhandoverReport: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [dateRange, setDateRange] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ start: null, end: null });
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [branches, setBranches] = useState<any[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Set default dates to today's date
  useEffect(() => {
    const today = dayjs();
    const startOfDay = today.startOf('day');
    const endOfDay = today.endOf('day');
    
    setDateRange({
      start: startOfDay,
      end: endOfDay
    });
  }, []);

  const fetchBranches = async () => {
    try {
      setBranchLoading(true);
      const response = await getTlocations_production_centre();
      console.log("Branches response:", response);
      setBranches(response.data.data || []);
      
      // Auto-select first branch if available
      if (response.data.data && response.data.data.length > 0) {
        setSelectedBranch(response.data.data[0].branch_id);
      }
    } catch (error) {
      console.log(error);
      alert("Failed to fetch branches");
    } finally {
      setBranchLoading(false);
    }
  };

  // Renamed this function to fetchFilteredStocks for clarity
  const fetchFilteredStocks = async () => {
    if (!dateRange.start || !dateRange.end) {
      alert('Please select both start and end dates');
      return;
    }

    if (!selectedBranch) {
      alert('Please select a branch');
      return;
    }

    // Validate that start date is before or equal to end date
    if (dateRange.start.isAfter(dateRange.end)) {
      alert('Start date cannot be after end date');
      return;
    }

    // Validate that dates are not in the future
    const now = dayjs();
    console.log('Now:', now.toISOString());
    console.log('Start Date:', dateRange.start.toISOString());
    console.log('End Date:', dateRange.end.toISOString());
    console.log('Selected Branch:', selectedBranch);
    
    if (dateRange.start.isAfter(now) || dateRange.end.isAfter(now)) {
      // alert('Dates cannot be in the future');
      // return;
    }

    try {
      setLoading(true);
      // Convert Dayjs objects to ISO strings for API call
      const startDateISO = dateRange.start.toISOString();
      const endDateISO = dateRange.end.toISOString();
      
      // Assuming your endpoint needs these parameters
      const response = await getBarInventory_sales(startDateISO, endDateISO, selectedBranch);
      console.log({ response });
      
      setStockItems(response.data);
      setIsFiltered(true);
    } catch (error) {
      console.error('Error fetching filtered stocks:', error);
      alert('Failed to fetch stock data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setStockItems([]);
    setIsFiltered(false);
    // Reset dates to today
    const today = dayjs();
    const startOfDay = today.startOf('day');
    const endOfDay = today.endOf('day');
    
    setDateRange({
      start: startOfDay,
      end: endOfDay
    });
    setSearchTerm('');
  };

  // Only filter items if we have filtered data
  const filteredItems = isFiltered 
    ? stockItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.category_name && item.category_name.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  // Get selected branch name for display
  const getSelectedBranchName = () => {
    const branch = branches.find(b => b.branch_id === selectedBranch);
    return branch ? branch.name : 'Unknown Branch';
  };

  return (
    <IonPage className='pushDown'>
      <IonContent>
        {/* Sticky Header Section */}
        <Box sx={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 1000, 
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          p: 3,
        }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Sales/Stock Items Report
          </Typography>

          {/* Branch Selection and Filter Controls */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Branch Selection Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }} disabled={branchLoading} size="small">
                <InputLabel id="branch-select-label">Select Branch</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={selectedBranch}
                  label="Select Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.branch_id} value={branch.branch_id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                color="primary"
                onClick={fetchBranches}
                disabled={branchLoading}
                size="small"
              >
                {branchLoading ? 'Refreshing...' : 'Refresh Branches'}
              </Button>
            </Box>

            {/* Search and Date Filter Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Search Items"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!isFiltered}
                sx={{ width: { xs: '100%', sm: '250px' } }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(newValue) =>
                    setDateRange((prev) => ({ ...prev, start: newValue }))
                  }
                  maxDate={dayjs()} // Prevent selecting future dates
                  renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />}
                  components={{
                    OpenPickerIcon: DateRange,
                  }}
                  disabled={loading || !selectedBranch}
                />
            
                <DatePicker
                  label="End Date"
                  value={dateRange.end}
                  onChange={(newValue) =>
                    setDateRange((prev) => ({ ...prev, end: newValue }))
                  }
                  minDate={dateRange.start}
                  maxDate={dayjs()} // Prevent selecting future dates
                  renderInput={(params) => <TextField {...params} size="small" sx={{ width: 140 }} />}
                  components={{
                    OpenPickerIcon: DateRange,
                  }}
                  disabled={loading || !selectedBranch}
                />

                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchFilteredStocks}
                  disabled={loading || !dateRange.start || !dateRange.end || !selectedBranch}
                  size="small"
                >
                  {loading ? 'Filtering...' : 'Filter'}
                </Button>

                {isFiltered && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearFilter}
                    disabled={loading}
                    size="small"
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
            </Box>

            {/* Selected Branch Info */}
            {selectedBranch && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                p: 1, 
                bgcolor: 'primary.light', 
                borderRadius: 1,
                width: 'fit-content'
              }}>
                <Typography variant="body2" color="primary.contrastText">
                  Selected Branch:
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary.contrastText">
                  {getSelectedBranchName()}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Scrollable Content Section */}
        <Box sx={{ p: 3 }}>
          {!selectedBranch && (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'warning.light', borderRadius: 2, mt: 2 }}>
              <Typography variant="h6" color="warning.dark">
                Please select a branch to view stock reports
              </Typography>
            </Box>
          )}

          {isFiltered && stockItems.length === 0 && !loading && selectedBranch && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No stock records found for {getSelectedBranchName()} in the selected date range
              </Typography>
            </Box>
          )}

          {loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                Loading stock data for {getSelectedBranchName()}...
              </Typography>
            </Box>
          )}

          {isFiltered && filteredItems.length > 0 && selectedBranch && (
            <>
              {/* Sticky Report Header */}
              <Box sx={{ 
                position: 'sticky', 
                top: 0, // Changed from 0 to dynamic height
                zIndex: 999, 
                mb: 3, 
                p: 2, 
                bgcolor: 'info.light', 
                borderRadius: 2,
                border: `1px solid ${theme.palette.info.main}30`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Stock Report for {getSelectedBranchName()}
                    </Typography>
                    <Typography variant="body2">
                      Date Range: {dateRange.start?.format('MMM D, YYYY')} to {dateRange.end?.format('MMM D, YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Total Items: {filteredItems.length}
                    </Typography>
                    <Chip 
                      label={`${filteredItems.filter(item => parseInt(item.totalAvailableAfterRange) < 10).length} Low Stock`}
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Table Container */}
              <Box sx={{ position: 'relative' }}>
                {/* Sticky Table Header */}
                <Box sx={{
                  position: 'sticky',
                  top: 0, // Sticks below the report header
                  zIndex: 998,
                  bgcolor: 'primary.main',
                  borderRadius: '8px 8px 0 0',
                  overflow: 'hidden',
                }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Product Name</TableCell>
                        {/* <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Total Posted</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Sold Before Start</TableCell> */}
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Available Before Range</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Sold In Range</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Available After Range</TableCell>
                        {/* <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5 }}>Status</TableCell> */}
                      </TableRow>
                    </TableHead>
                  </Table>
                </Box>

                {/* Scrollable Table Body */}
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    maxHeight: '500px', 
                    overflow: 'auto',
                    borderRadius: '0 0 8px 8px',
                    borderTop: 'none',
                  }}
                >
                  <Table>
                    <TableBody>
                      {filteredItems.map((item) => (
                        <TableRow key={item.product_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'medium' }}>{item.name}</TableCell>
                          {/* <TableCell>{item.totalQtyPosted}</TableCell>
                          <TableCell>{item.totalQtySoldBeforeStart}</TableCell> */}
                          <TableCell>{item.totalAvailableBeforeRange}</TableCell>
                          <TableCell>{item.totalQtySoldInRange}</TableCell>
                          <TableCell>{item.totalAvailableAfterRange}</TableCell>
                          {/* <TableCell>
                            <Box sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1,
                              bgcolor: parseInt(item.totalAvailableAfterRange) < 10 ? '#ffebee' : '#e8f5e9',
                              color: parseInt(item.totalAvailableAfterRange) < 10 ? '#c62828' : '#2e7d32',
                              fontWeight: 'medium',
                              fontSize: '0.875rem'
                            }}>
                              {parseInt(item.totalAvailableAfterRange) < 10 ? '⚠️ Low Stock' : '✅ Available'}
                            </Box>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Summary Statistics */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Summary Statistics
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 150, p: 1, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="body2" color="textSecondary">Low Stock Items</Typography>
                    <Typography variant="h6" color="error.main">
                      {filteredItems.filter(item => parseInt(item.totalAvailableAfterRange) < 10).length}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 150, p: 1, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="body2" color="textSecondary">Available Items</Typography>
                    <Typography variant="h6" color="success.main">
                      {filteredItems.filter(item => parseInt(item.totalAvailableAfterRange) >= 10).length}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 150, p: 1, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="body2" color="textSecondary">Total Items Sold in Range</Typography>
                    <Typography variant="h6" color="primary.main">
                      {filteredItems.reduce((sum, item) => sum + parseInt(item.totalQtySoldInRange), 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 150, p: 1, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                    <Typography variant="body2" color="textSecondary">Total Items Available</Typography>
                    <Typography variant="h6" color="info.main">
                      {filteredItems.reduce((sum, item) => sum + parseInt(item.totalAvailableAfterRange), 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {!isFiltered && !loading && selectedBranch && (
            <Box sx={{ textAlign: 'center', py: 4, mt: 2 }}>
              <Typography variant="h6" color="textSecondary">
                Please select date range and click "Filter" to view stock records for {getSelectedBranchName()}
              </Typography>
            </Box>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default BarhandoverReport;